import { privateClient } from "@/api/privateClient";
import { SUPER_ADMIN_ENDPOINTS } from "../../endpoint";
import type { WhatsappSettings, InstitutionWallet, MessageTemplate, WhatsappTriggerType, PlatformDevice, WhatsappMessageLog, FeeReminderLog } from "../types";

export const fetchWhatsappSettings = async (): Promise<WhatsappSettings> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.GET_SETTINGS);
  return data;
};

export interface PlatformTemplate {
  key: string;
  label: string;
  placeholders: string[];
  defaultTemplate: string;
  template: string;
  isCustom: boolean;
}

export const fetchPlatformTemplates = async (): Promise<PlatformTemplate[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.PLATFORM_TEMPLATES);
  return data.templates;
};

export const savePlatformTemplate = async (key: string, template: string): Promise<PlatformTemplate[]> => {
  const { data } = await privateClient.put(`${SUPER_ADMIN_ENDPOINTS.WHATSAPP.PLATFORM_TEMPLATES}/${key}`, { template });
  return data.templates;
};

export const resetPlatformTemplate = async (key: string): Promise<PlatformTemplate[]> => {
  const { data } = await privateClient.delete(`${SUPER_ADMIN_ENDPOINTS.WHATSAPP.PLATFORM_TEMPLATES}/${key}`);
  return data.templates;
};

export const resendPlatformLogin = async (target: { organizationId?: number; institutionId?: number }): Promise<void> => {
  await privateClient.post(SUPER_ADMIN_ENDPOINTS.WHATSAPP.RESEND_LOGIN, target);
};

export const updateWhatsappSettings = async (payload: { baseUrl?: string; embedBaseUrl?: string; mobileAppUrl?: string; webLoginUrl?: string; sendingPaused?: boolean }): Promise<WhatsappSettings> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.WHATSAPP.UPDATE_SETTINGS, payload);
  return data;
};

export interface WalletPage {
  institutionWallets: InstitutionWallet[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const fetchInstitutionWallets = async (params: { page: number; limit: number; search?: string }): Promise<WalletPage> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.LIST_WALLETS, { params });
  return data;
};

export const fetchInstitutionWallet = async (institutionId: number): Promise<InstitutionWallet> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.GET_WALLET(institutionId));
  return data.wallet;
};

export const linkDevice = async (institutionId: number, apiKey: string, virtualNumberId: string): Promise<InstitutionWallet> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.WHATSAPP.LINK_DEVICE(institutionId), { apiKey, virtualNumberId });
  return data.wallet;
};

export const setSchoolBlocked = async (institutionId: number, blocked: boolean): Promise<InstitutionWallet> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.WHATSAPP.BLOCKED(institutionId), { blocked });
  return data.wallet;
};

export const topUpTokens = async (institutionId: number, amount: number): Promise<InstitutionWallet> => {
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.WHATSAPP.TOP_UP(institutionId), { amount });
  return data.wallet;
};

export const updateTriggers = async (
  institutionId: number,
  triggers: Partial<InstitutionWallet["triggers"]>
): Promise<InstitutionWallet> => {
  const { data } = await privateClient.patch(SUPER_ADMIN_ENDPOINTS.WHATSAPP.TRIGGERS(institutionId), triggers);
  return data.wallet;
};

/** The provider gives us no webhook/status API — an admin confirms the scan succeeded (from the embedded login widget) and this marks it here. */
export const markConnectionStatus = async (
  institutionId: number,
  status: "CONNECTED" | "DISCONNECTED"
): Promise<InstitutionWallet> => {
  const { data } = await privateClient.patch(SUPER_ADMIN_ENDPOINTS.WHATSAPP.CONNECTION_STATUS(institutionId), { status });
  return data.wallet;
};

export const fetchMessageTemplates = async (institutionId: number): Promise<MessageTemplate[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.LIST_TEMPLATES(institutionId));
  return data.templates;
};

export const saveMessageTemplate = async (
  institutionId: number,
  triggerType: WhatsappTriggerType,
  template: string
): Promise<MessageTemplate> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.WHATSAPP.SAVE_TEMPLATE(institutionId, triggerType), { template });
  return data;
};

export const resetMessageTemplate = async (institutionId: number, triggerType: WhatsappTriggerType): Promise<MessageTemplate> => {
  const { data } = await privateClient.delete(SUPER_ADMIN_ENDPOINTS.WHATSAPP.RESET_TEMPLATE(institutionId, triggerType));
  return data;
};

export const fetchPlatformDevice = async (): Promise<PlatformDevice> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.GET_PLATFORM_DEVICE);
  return data.device;
};

export const linkPlatformDevice = async (apiKey: string, virtualNumberId: string): Promise<PlatformDevice> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.WHATSAPP.LINK_PLATFORM_DEVICE, { apiKey, virtualNumberId });
  return data.device;
};

export const fetchPlatformLoginWidgetUrl = async (): Promise<{ embedUrl: string | null; error: string | null }> => {
  try {
    const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.PLATFORM_LOGIN_WIDGET_URL);
    return { embedUrl: data.embedUrl ?? null, error: null };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    return { embedUrl: null, error: err?.response?.data?.error || "Failed to load QR" };
  }
};

export const fetchMessageLogs = async (institutionId: number): Promise<WhatsappMessageLog[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.MESSAGE_LOGS(institutionId));
  return data.logs;
};

export const fetchFeeReminderLogs = async (institutionId: number): Promise<FeeReminderLog[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.WHATSAPP.FEE_REMINDER_LOGS, { params: { institutionId } });
  return data.logs;
};
